import React, { useEffect, useMemo, useState } from "react";
import { StyleSheet, View, Text, useWindowDimensions } from "react-native";
import { BackgroundGradient } from "../components/BackgroundGradient";
import { ThemeOptions, useTheme } from "../providers/ThemeProvider";
import { useComponent, WeatherResponse } from "../providers/ConfigProvider";
import i18n from "i18next";
import { Path, Svg } from "react-native-svg";
import { getIcon, IconCode } from "../components/WeatherWidget";
import { RainIcon, TemperatureIcon, WindIcon } from "../components/Icons";
import dayjs from "dayjs";
import { useDateFormatter } from "../hooks/useDateFormatter";
import { Spacer } from "../components/ViewComponents";
import { useTranslation } from "react-i18next";
import { ErrorScreen } from "../screens/ErrorScreen";
import { config } from "../config";
import { TemplateProps } from "../../types";
import { TopBar } from "../components/TopBar";

const makeLayoutStyles = (theme: ThemeOptions) => {
  return StyleSheet.create({
    page: {
      flex: 1,
    },
    container: {
      flex: 1,
      flexDirection: "column",
    },
    mainContent: {
      flex: 1,
      flexDirection: "row",
      paddingHorizontal: theme.spacing.regular,
      paddingBottom: theme.spacing.medium,
    },
    dailyOverviewBar: {
      flexDirection: "row",
      justifyContent: "space-between",
      padding: theme.spacing.small,
      paddingBottom: theme.spacing.medium,
    },
    dailyDetailedViewContainer: {
      paddingVertical: theme.spacing.small,
      width: "45%",
      backgroundColor: theme.toRgba(theme.contrastColor, 0.15),
      borderWidth: theme.borderWidth.large,
      borderRadius: theme.borderRadius.large,
      borderColor: theme.toRgba(theme.contrastColor, 0.1),
    },
    currentDayOverview: {
      backgroundColor: theme.toRgba(theme.contrastColor, 0.15),
      width: "55%",
      borderColor: theme.toRgba(theme.contrastColor, 0.15),
      borderWidth: theme.borderWidth.large,
      borderRadius: theme.borderRadius.large,
    },
    locationText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.medium,
      fontWeight: "600",
    },
    dateText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.body,
      fontWeight: "600",
    },
    currentWeatherLocationText: {
      color: theme.backgroundText,
      fontSize: theme.fontSize.h3,
      fontWeight: "500",
    },
    weatherOverview: {
      paddingHorizontal: theme.spacing.medium,
      flexDirection: "row",
      flex: 1,
    },
    dailyOverviewContainer: {
      backgroundColor: theme.toRgba(theme.contrastColor, 0.15),
      borderColor: theme.toRgba(theme.contrastColor, 0.2),
      borderWidth: theme.borderWidth.large,
      borderRadius: theme.borderRadius.large,
      flexGrow: 1,
      paddingVertical: theme.spacing.small,
      alignItems: "center",
      flexDirection: "column",
    },
    mainWeatherContainer: {
      flex: 1,
      flexDirection: "column",
      alignItems: "center",
      padding: theme.spacing.medium,
      justifyContent: "center",
    },
    boldText: {
      fontSize: theme.fontSize.h1,
      color: theme.backgroundText,
      fontWeight: "bold",
    },
    weatherMediumText: {
      fontSize: theme.fontSize.large,
      color: theme.backgroundText,
      fontWeight: "400",
    },
    rainWindText: {
      fontSize: theme.fontSize.large,
      color: theme.backgroundText,
      fontWeight: "600",
    },
    weatherLargeText: {
      fontSize: theme.fontSize.h4,
      color: theme.backgroundText,
      fontWeight: "600",
    },
    detailRow: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "space-between",
      borderBottomWidth: theme.borderWidth.regular,
      borderBottomColor: theme.toRgba(theme.contrastColor, 0.1),
      height: 40, // Fixed row height
    },
    detailCell: {
      flexDirection: "row",
      width: "25%",
      alignItems: "center",
    },
    hourContainer: {
      flex: 1,
      paddingHorizontal: theme.spacing.medium,
    },
  });
};

const weatherConditionsMap = {
  Clear: "Clear",
  Clouds: "Clouds",
  Rain: "Rain",
  Drizzle: "Drizzle",
  Thunderstorm: "Thunderstorm",
  Snow: "Snow",
  Mist: "Mist",
  Smoke: "Smoke",
  Haze: "Haze",
  Dust: "Dust",
  Fog: "Fog",
  Sand: "Sand",
  Ash: "Ash",
  Squall: "Squall",
  Tornado: "Tornado",
} as const;

type WeatherCondition = keyof typeof weatherConditionsMap;

const CurrentWeatherView = ({
  currentWeather,
}: {
  currentWeather?: WeatherResponse;
}) => {
  const { t } = useTranslation("Weather");
  const theme = useTheme();
  const styles = makeLayoutStyles(theme);
  const formatter = useDateFormatter();
  const { width, height } = useWindowDimensions();
  if (currentWeather == undefined) {
    return <Text> No Location data found</Text>;
  }

  const isToday = dayjs(currentWeather.date).isSame(dayjs(), "day");

  const wind = Math.round(currentWeather.weather.wind * 2.237); // m/s to mph conversion
  const condition =
    currentWeather.weather.weatherCondition in weatherConditionsMap
      ? t(currentWeather.weather.weatherCondition as WeatherCondition)
      : currentWeather.weather.weatherCondition;
  const iconDimensions = Math.min(width, height) * (35 / 100);
  return (
    <View style={styles.mainWeatherContainer}>
      <Text style={styles.currentWeatherLocationText}>
        {`${isToday ? t("Today") : formatter.dateDay(dayjs(currentWeather.date).toDate())} - ${currentWeather.location}`}
      </Text>
      <View
        style={{
          flexDirection: "row",
          alignItems: "center",
          padding: theme.spacing.small,
        }}
      >
        <Svg width={iconDimensions} height={iconDimensions} viewBox="0 0 42 32">
          <Path
            d={getIcon(currentWeather.weather.icon as IconCode)}
            fill={theme.backgroundText}
          />
        </Svg>
        <Spacer size="medium" />
        <View
          style={{
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
            <Text style={styles.boldText}>
              {getTemperature(currentWeather.weather.temperature)}
              {"\u00b0"}
            </Text>
          </View>
          <Text style={styles.weatherLargeText}>{condition}</Text>
          <Spacer size="small" />
          <View style={{ flexDirection: "row" }}>
            <RainIcon
              height={theme.iconSize.medium}
              width={theme.iconSize.medium}
              fill={theme.backgroundText}
            />
            <Spacer size="xxsmall" />
            <Text style={styles.rainWindText}>
              {Math.round(currentWeather.weather.pop)}%
            </Text>
            <Spacer size="medium" />
            <WindIcon
              height={theme.iconSize.medium}
              width={theme.iconSize.medium}
              fill={theme.backgroundText}
            />
            <Spacer size="xxsmall" />
            <Text style={styles.rainWindText}>{`${wind} mph`}</Text>
          </View>
        </View>
      </View>
    </View>
  );
};

const WeatherOverviewBanner = ({
  dailyWeather,
  currentIndex,
}: {
  dailyWeather: WeatherResponse[];
  currentIndex: number;
}) => {
  const theme = useTheme();
  const styles = makeLayoutStyles(theme);
  const formatter = useDateFormatter();
  return (
    <View style={styles.weatherOverview}>
      {dailyWeather.map((day, index) => {
        return (
          <View
            key={index}
            style={[
              styles.dailyOverviewContainer,
              index === currentIndex && { borderColor: theme.contrastColor },
              {
                marginRight:
                  index === dailyWeather.length - 1 ? 0 : theme.spacing.medium,
              },
            ]}
          >
            <Text style={styles.locationText}>
              {formatter.dateDay(dayjs(day.date).toDate())}
            </Text>
            <View
              style={{
                flexDirection: "row",
                paddingTop: theme.spacing.xxsmall,
                alignItems: "center",
              }}
            >
              <Svg
                height={theme.iconSize.large}
                width={theme.iconSize.large}
                viewBox="0 0 42 32"
              >
                <Path
                  d={getIcon(day.weather.icon as IconCode)}
                  fill={theme.backgroundText}
                />
              </Svg>
            </View>
            <View style={{ flexDirection: "row", alignItems: "flex-start" }}>
              <Text style={styles.currentWeatherLocationText}>
                {getTemperature(day.weather.temperature)}
                {"\u00b0"}
              </Text>
            </View>
            <Text style={styles.dateText}>{day.location}</Text>
          </View>
        );
      })}
    </View>
  );
};

const getTemperature = (temperature: string): number => {
  const tempInCelsius = Number(temperature);

  if (isNaN(tempInCelsius)) {
    throw new Error("Invalid temperature value");
  }

  return i18n.language === "en-US"
    ? Math.round((tempInCelsius * 9) / 5 + 32)
    : Math.round(tempInCelsius);
};

const HourlyWeather = ({
  hourlyWeather,
}: {
  hourlyWeather: WeatherResponse[];
}) => {
  const theme = useTheme();
  const styles = makeLayoutStyles(theme);
  const formatter = useDateFormatter();
  const [containerHeight, setContainerHeight] = useState<number>(0);
  const [visibleItems, setVisibleItems] = useState<WeatherResponse[]>([]);
  const filteredData = useMemo(() => {
    return hourlyWeather.filter((hour) => {
      const time = dayjs(hour.date).format("HH:mm");
      return isDesiredTime(time);
    });
  }, [hourlyWeather]);

  useEffect(() => {
    if (containerHeight > 0) {
      const rowHeight = 40; // Approximate height of one row
      const maxRows = Math.floor(containerHeight / rowHeight);
      setVisibleItems(filteredData.slice(0, maxRows));
    }
  }, [containerHeight, filteredData]);

  return (
    <View
      style={styles.hourContainer}
      onLayout={(event) => {
        const { height } = event.nativeEvent.layout;
        setContainerHeight(height);
      }}
    >
      {visibleItems.map((hour, index) => {
        const wind = Math.round(hour.weather.wind * 2.237); // m/s to mph conversion
        return (
          <View key={index} style={styles.detailRow}>
            <View style={{ width: "25%" }}>
              <Text style={[styles.weatherMediumText]}>
                {formatter.hoursMinutes(dayjs(hour.date).toDate())}
              </Text>
            </View>
            <Spacer size="small" />
            <View style={styles.detailCell}>
              <TemperatureIcon
                height={theme.iconSize.medium}
                width={theme.iconSize.medium}
                fill={theme.backgroundText}
              />
              <Spacer size="xxsmall" />
              <Text style={styles.weatherMediumText}>
                {getTemperature(hour.weather.temperature)}
                {"\u00b0"}
              </Text>
            </View>
            <Spacer size="small" />
            <View style={styles.detailCell}>
              <RainIcon
                height={theme.iconSize.medium}
                width={theme.iconSize.medium}
                fill={theme.backgroundText}
              />
              <Spacer size="xxsmall" />
              <Text style={styles.weatherMediumText}>
                {Math.round(hour.weather.pop)}%
              </Text>
            </View>
            <Spacer size="small" />
            <View style={styles.detailCell}>
              <WindIcon
                height={theme.iconSize.medium}
                width={theme.iconSize.medium}
                fill={theme.backgroundText}
              />
              <Spacer size="xxsmall" />
              <Text style={styles.weatherMediumText}>{`${wind} mph`}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const isDesiredTime = (time: string) => {
  const allowedTimes = [
    "06:00",
    "08:00",
    "10:00",
    "12:00",
    "13:00",
    "14:00",
    "15:00",
    "16:00",
    "17:00",
    "18:00",
    "19:00",
    "20:00",
    "22:00",
  ];
  return allowedTimes.includes(time);
};

export const Weather = ({ onLoopComplete }: TemplateProps) => {
  const { t } = useTranslation("Weather");
  const theme = useTheme();
  const styles = makeLayoutStyles(theme);
  const weatherData = useComponent("weather", "weather");
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const nextIndex =
        (currentIndex + 1) %
        Math.min(weatherData?.dailyBreakdown.length || 0, 4);
      setCurrentIndex(nextIndex);
      if (nextIndex === 0) {
        onLoopComplete();
      }
    }, config.weather.changePageRate);

    return () => clearInterval(interval);
  }, [weatherData, currentIndex, onLoopComplete]);

  if (weatherData == null) {
    return (
      <ErrorScreen
        message={t(
          "No weather data to show, please contact your Teamworks administrator.",
        )}
      />
    );
  }

  const hourlyWeather = weatherData.hourlyBreakdown.filter((h) =>
    dayjs(h.date).isSame(
      dayjs(weatherData.dailyBreakdown[currentIndex].date),
      "day",
    ),
  );

  return (
    <View style={styles.page}>
      <BackgroundGradient background={theme.background} accent={theme.accent} />
      <View style={styles.container}>
        <TopBar />
        <View style={styles.dailyOverviewBar}>
          <WeatherOverviewBanner
            dailyWeather={weatherData.dailyBreakdown}
            currentIndex={currentIndex}
          />
        </View>
        <View style={styles.mainContent}>
          <View style={styles.currentDayOverview}>
            <CurrentWeatherView
              currentWeather={weatherData.dailyBreakdown[currentIndex]}
            />
          </View>
          <Spacer size="small" />
          <View style={styles.dailyDetailedViewContainer}>
            <HourlyWeather hourlyWeather={hourlyWeather} />
          </View>
        </View>
      </View>
    </View>
  );
};
